import { useState, useEffect, useCallback } from "react"
import { useBranding } from "../hooks/useBranding"

// Live-preview branding editor for a client workspace.
// Shows settings on the left, form preview on the right with CSS custom properties applied.

export function BrandingEditor({ workspaceId, workspaceName }) {
    const {
        branding, domain, emailDomain, loading,
        fetchBranding, updateBranding,
        addDomain, verifyDomain, removeDomain,
        addEmailDomain, verifyEmailDomain
    } = useBranding()

    const [tab, setTab] = useState("branding") // branding | domain | email
    const [form, setForm] = useState(null)
    const [saving, setSaving] = useState(false)
    const [saveMsg, setSaveMsg] = useState("")

    useEffect(() => {
        if (workspaceId) {
            fetchBranding(workspaceId).then(data => {
                if (data?.branding) setForm(data.branding)
            })
        }
    }, [workspaceId, fetchBranding])

    useEffect(() => {
        if (branding && !form) setForm(branding)
    }, [branding, form])

    const handleChange = useCallback((key, value) => {
        setForm(prev => ({ ...prev, [key]: value }))
    }, [])

    const handleSave = async () => {
        setSaving(true)
        setSaveMsg("")
        try {
            await updateBranding(workspaceId, form)
            setSaveMsg("Saved")
            setTimeout(() => setSaveMsg(""), 2000)
        } catch (e) {
            setSaveMsg("Error: " + e.message)
        } finally {
            setSaving(false)
        }
    }

    if (loading && !form) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            </div>
        )
    }

    return (
        <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        White-label Branding
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {workspaceName}
                    </p>
                </div>
                {tab === "branding" && (
                    <div className="flex items-center gap-3">
                        {saveMsg && (
                            <span className={`text-sm ${saveMsg.startsWith("Error") ? "text-red-600" : "text-green-600"}`}>
                                {saveMsg}
                            </span>
                        )}
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition"
                        >
                            {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                )}
            </div>

            {/* Tab bar */}
            <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
                {[
                    { id: "branding", label: "Brand & Colors" },
                    { id: "domain", label: "Custom Domain" },
                    { id: "email", label: "Email Sender" }
                ].map(t => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
                            tab === t.id
                                ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
                                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "branding" && form && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Settings panel */}
                    <div className="space-y-6">
                        <Section title="Identity">
                            <Field label="Company name">
                                <input
                                    type="text"
                                    value={form.companyName || ""}
                                    onChange={e => handleChange("companyName", e.target.value)}
                                    placeholder="Acme Corp"
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Logo URL">
                                <input
                                    type="url"
                                    value={form.logoUrl || ""}
                                    onChange={e => handleChange("logoUrl", e.target.value)}
                                    placeholder="https://example.com/logo.png"
                                    className={inputCls}
                                />
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Paste a direct image URL (PNG/SVG, recommended 200×60px)</p>
                            </Field>
                            <Field label="Favicon URL">
                                <input
                                    type="url"
                                    value={form.faviconUrl || ""}
                                    onChange={e => handleChange("faviconUrl", e.target.value)}
                                    placeholder="https://example.com/favicon.png"
                                    className={inputCls}
                                />
                            </Field>
                        </Section>

                        <Section title="Colors">
                            <div className="grid grid-cols-2 gap-4">
                                <ColorField label="Primary" value={form.primaryColor} onChange={v => handleChange("primaryColor", v)} />
                                <ColorField label="Accent" value={form.accentColor} onChange={v => handleChange("accentColor", v)} />
                                <ColorField label="Background" value={form.backgroundColor} onChange={v => handleChange("backgroundColor", v)} />
                                <ColorField label="Text" value={form.textColor} onChange={v => handleChange("textColor", v)} />
                            </div>
                        </Section>

                        <Section title="Typography">
                            <Field label="Font family">
                                <select
                                    value={form.fontFamily || ""}
                                    onChange={e => handleChange("fontFamily", e.target.value)}
                                    className={inputCls}
                                >
                                    <option value="">System default</option>
                                    <option value="Inter">Inter</option>
                                    <option value="Roboto">Roboto</option>
                                    <option value="Open Sans">Open Sans</option>
                                    <option value="Lato">Lato</option>
                                    <option value="Poppins">Poppins</option>
                                    <option value="Montserrat">Montserrat</option>
                                    <option value="Raleway">Raleway</option>
                                </select>
                            </Field>
                        </Section>

                        <Section title="Footer">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <div
                                    onClick={() => handleChange("removeBranding", !form.removeBranding)}
                                    className={`relative w-10 h-5 rounded-full transition ${form.removeBranding ? "bg-indigo-600" : "bg-gray-300 dark:bg-gray-600"}`}
                                >
                                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.removeBranding ? "translate-x-5" : ""}`} />
                                </div>
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Remove "Powered by ContentFlashAI" footer
                                </span>
                            </label>
                            {form.removeBranding && (
                                <Field label="Custom footer text (optional)">
                                    <input
                                        type="text"
                                        value={form.footerText || ""}
                                        onChange={e => handleChange("footerText", e.target.value)}
                                        placeholder="© 2026 Acme Corp. All rights reserved."
                                        className={inputCls}
                                        maxLength={500}
                                    />
                                </Field>
                            )}
                        </Section>

                        <Section title="Custom CSS">
                            <textarea
                                value={form.customCss || ""}
                                onChange={e => handleChange("customCss", e.target.value)}
                                rows={8}
                                placeholder={`.cfai-form-title { font-size: 2rem; }\n.cfai-submit-btn { border-radius: 4px; }`}
                                className={`${inputCls} font-mono text-xs`}
                                maxLength={10000}
                            />
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                Raw CSS injected into the form page. @import and javascript: are stripped.
                            </p>
                        </Section>
                    </div>

                    {/* Live preview */}
                    <div className="lg:sticky lg:top-8 h-fit">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                            Live preview
                        </p>
                        <BrandingPreview branding={form} />
                    </div>
                </div>
            )}

            {tab === "domain" && (
                <CustomDomainTab
                    workspaceId={workspaceId}
                    domain={domain}
                    onAdd={addDomain}
                    onVerify={verifyDomain}
                    onRemove={removeDomain}
                />
            )}

            {tab === "email" && (
                <EmailDomainTab
                    workspaceId={workspaceId}
                    emailDomain={emailDomain}
                    onAdd={addEmailDomain}
                    onVerify={verifyEmailDomain}
                />
            )}
        </div>
    )
}

function BrandingPreview({ branding }) {
    const style = {
        "--form-primary": branding?.primaryColor || "#6366f1",
        "--form-accent": branding?.accentColor || "#8b5cf6",
        "--form-bg": branding?.backgroundColor || "#ffffff",
        "--form-text": branding?.textColor || "#1f2937",
        fontFamily: branding?.fontFamily ? `'${branding.fontFamily}', sans-serif` : "inherit",
        backgroundColor: branding?.backgroundColor || "#ffffff",
        color: branding?.textColor || "#1f2937"
    }

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
            {/* Form header */}
            <div className="p-6" style={style}>
                {branding?.logoUrl && (
                    <img
                        src={branding.logoUrl}
                        alt="logo"
                        className="h-8 object-contain mb-4"
                        onError={e => { e.target.style.display = "none" }}
                    />
                )}
                <h2 className="text-xl font-bold mb-1" style={{ color: style["--form-text"] }}>
                    Contact information
                </h2>
                <p className="text-sm opacity-70">Sample form preview</p>

                <div className="mt-5 space-y-3">
                    <label className="block">
                        <span className="text-sm font-medium block mb-1" style={{ color: style["--form-text"] }}>
                            Your name
                        </span>
                        <div
                            className="w-full px-3 py-2 rounded-lg border text-sm"
                            style={{ borderColor: style["--form-primary"], color: style["--form-text"], background: "rgba(0,0,0,0.03)" }}
                        >
                            &nbsp;
                        </div>
                    </label>
                    <label className="block">
                        <span className="text-sm font-medium block mb-1" style={{ color: style["--form-text"] }}>
                            Email address
                        </span>
                        <div
                            className="w-full px-3 py-2 rounded-lg border text-sm"
                            style={{ borderColor: style["--form-primary"], color: style["--form-text"], background: "rgba(0,0,0,0.03)" }}
                        >
                            &nbsp;
                        </div>
                    </label>
                </div>

                <button
                    className="mt-5 w-full py-2 rounded-lg text-white text-sm font-semibold transition"
                    style={{ backgroundColor: style["--form-primary"] }}
                >
                    Submit →
                </button>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 text-center">
                {branding?.removeBranding ? (
                    branding?.footerText
                        ? <p className="text-xs text-gray-500">{branding.footerText}</p>
                        : null
                ) : (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Powered by <span className="font-semibold text-indigo-600">ContentFlashAI</span>
                    </p>
                )}
            </div>

            {/* Custom CSS preview injection */}
            {branding?.customCss && (
                <style>{branding.customCss}</style>
            )}
        </div>
    )
}

function CustomDomainTab({ workspaceId, domain, onAdd, onVerify, onRemove }) {
    const [newDomain, setNewDomain] = useState("")
    const [working, setWorking] = useState(false)
    const [msg, setMsg] = useState(null)
    const handleAdd = async () => {
        setWorking(true)
        setMsg(null)
        try {
            await onAdd(workspaceId, newDomain)
            setMsg({ ok: true, text: "Domain registered. Add the DNS records below, then click Verify." })
        } catch (e) {
            setMsg({ ok: false, text: e.message })
        } finally {
            setWorking(false)
        }
    }

    const handleVerify = async () => {
        setWorking(true)
        setMsg(null)
        try {
            const res = await onVerify(workspaceId)
            if (res.verified) {
                setMsg({ ok: true, text: "Domain verified successfully! SSL will be provisioned automatically." })
            } else {
                setMsg({ ok: false, text: res.error || "Verification failed — check your DNS records and try again." })
            }
        } catch (e) {
            setMsg({ ok: false, text: e.message })
        } finally {
            setWorking(false)
        }
    }

    const handleRemove = async () => {
        if (!window.confirm("Remove this custom domain?")) return
        setWorking(true)
        try {
            await onRemove(workspaceId)
            setMsg({ ok: true, text: "Domain removed." })
        } catch (e) {
            setMsg({ ok: false, text: e.message })
        } finally {
            setWorking(false)
        }
    }

    return (
        <div className="max-w-2xl space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4 text-sm text-blue-800 dark:text-blue-300">
                <strong>How it works:</strong> Your forms are served at{" "}
                <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">yourdomain.com/f/&lt;formId&gt;</code>.
                After registering, add <strong>both</strong> the CNAME and TXT records shown below — the CNAME
                routes traffic and the TXT record verifies you own the domain. SSL is then provisioned
                automatically via Let&apos;s Encrypt once the records propagate.
            </div>

            {msg && (
                <div className={`rounded-xl p-4 text-sm ${msg.ok ? "bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700" : "bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-700"}`}>
                    {msg.text}
                </div>
            )}

            {!domain ? (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
                    <h3 className="font-medium text-gray-900 dark:text-white">Add custom domain</h3>
                    <div className="flex gap-3">
                        <input
                            type="text"
                            value={newDomain}
                            onChange={e => setNewDomain(e.target.value)}
                            placeholder="forms.yourdomain.com"
                            className={inputCls + " flex-1"}
                            onKeyDown={e => e.key === "Enter" && handleAdd()}
                        />
                        <button
                            onClick={handleAdd}
                            disabled={working || !newDomain.trim()}
                            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition"
                        >
                            {working ? "Adding…" : "Add domain"}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-gray-900 dark:text-white">{domain.domain}</p>
                            <StatusBadge status={domain.status} />
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={handleVerify}
                                disabled={working || domain.status === "active"}
                                className="px-3 py-1.5 border border-indigo-300 text-indigo-700 dark:border-indigo-600 dark:text-indigo-400 text-sm rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-40 transition"
                            >
                                {working ? "Checking…" : "Verify now"}
                            </button>
                            <button
                                onClick={handleRemove}
                                disabled={working}
                                className="px-3 py-1.5 border border-red-300 text-red-600 text-sm rounded-lg hover:bg-red-50 disabled:opacity-40 transition"
                            >
                                Remove
                            </button>
                        </div>
                    </div>

                    {domain.status !== "active" && (
                        <div className="space-y-3">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Add <strong>both</strong> of these DNS records to your domain:</p>
                            <DnsRecord
                                type="CNAME"
                                name={domain.domain}
                                value="forms.contentflashai.com"
                                purpose="Points your domain to our servers"
                            />
                            <DnsRecord
                                type="TXT"
                                name={`_cfai-verify.${domain.domain}`}
                                value={domain.verifyToken}
                                purpose="Proves you own this domain"
                                required
                            />
                            <p className="text-sm text-gray-600 dark:text-gray-400">DNS changes can take up to 48 hours to propagate. We check automatically every hour.</p>
                        </div>
                    )}

                    {domain.status === "active" && (
                        <div className="space-y-2">
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                SSL status: <SslBadge status={domain.sslStatus} />
                            </p>
                            <p className="text-sm text-gray-500">
                                Your forms are live at{" "}
                                <a href={`https://${domain.domain}`} target="_blank" rel="noreferrer" className="text-indigo-600 underline">
                                    https://{domain.domain}
                                </a>
                            </p>
                            <EmbedCode domain={domain.domain} />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

function EmailDomainTab({ workspaceId, emailDomain, onAdd, onVerify }) {
    const [newDomain, setNewDomain] = useState("")
    const [fromName, setFromName] = useState("")
    const [working, setWorking] = useState(false)
    const [msg, setMsg] = useState(null)
    const handleAdd = async () => {
        setWorking(true)
        setMsg(null)
        try {
            await onAdd(workspaceId, {
                domain: newDomain,
                fromName,
                fromEmail: `noreply@${newDomain}`
            })
            setMsg({ ok: true, text: "Email domain registered. Add the DNS records below, then click Verify." })
        } catch (e) {
            setMsg({ ok: false, text: e.message })
        } finally {
            setWorking(false)
        }
    }

    const handleVerify = async () => {
        setWorking(true)
        setMsg(null)
        try {
            const res = await onVerify(workspaceId)
            const { results, allVerified } = res
            if (allVerified) {
                setMsg({ ok: true, text: "All records verified! Emails will now send from your domain." })
            } else {
                const missing = ["spf", "dkim", "dmarc"].filter(k => !results[k]).join(", ").toUpperCase()
                setMsg({ ok: false, text: `Missing records: ${missing}. DNS propagation can take up to 48 hours.` })
            }
        } catch (e) {
            setMsg({ ok: false, text: e.message })
        } finally {
            setWorking(false)
        }
    }

    return (
        <div className="max-w-2xl space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4 text-sm text-blue-800 dark:text-blue-300">
                <strong>Custom email sender:</strong> Form notification emails will arrive from{" "}
                <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">noreply@yourdomain.com</code>{" "}
                instead of ContentFlashAI&apos;s address.
            </div>

            {msg && (
                <div className={`rounded-xl p-4 text-sm ${msg.ok ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
                    {msg.text}
                </div>
            )}

            {!emailDomain ? (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
                    <h3 className="font-medium text-gray-900 dark:text-white">Add email sender domain</h3>
                    <Field label="From name (shown to email recipients)">
                        <input
                            type="text"
                            value={fromName}
                            onChange={e => setFromName(e.target.value)}
                            placeholder="Acme Corp"
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Domain">
                        <input
                            type="text"
                            value={newDomain}
                            onChange={e => setNewDomain(e.target.value)}
                            placeholder="yourdomain.com"
                            className={inputCls}
                        />
                    </Field>
                    <button
                        onClick={handleAdd}
                        disabled={working || !newDomain.trim()}
                        className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition"
                    >
                        {working ? "Adding…" : "Add email domain"}
                    </button>
                </div>
            ) : (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                                {emailDomain.fromName && <span className="text-gray-600 dark:text-gray-400 mr-2">{emailDomain.fromName}</span>}
                                &lt;noreply@{emailDomain.domain}&gt;
                            </p>
                            <StatusBadge status={emailDomain.status} />
                        </div>
                        <button
                            onClick={handleVerify}
                            disabled={working || emailDomain.status === "active"}
                            className="px-3 py-1.5 border border-indigo-300 text-indigo-700 dark:border-indigo-600 dark:text-indigo-400 text-sm rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-40 transition"
                        >
                            {working ? "Checking…" : "Check records"}
                        </button>
                    </div>

                    {emailDomain.status !== "active" && (
                        <div className="space-y-3">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Add these DNS records:</p>
                            <VerifiedRow label="SPF" verified={emailDomain.spfVerified}>
                                <DnsRecord
                                    type="TXT"
                                    name={emailDomain.domain}
                                    value="v=spf1 include:mail.contentflashai.com ~all"
                                    purpose="Authorizes us to send email on your behalf"
                                />
                            </VerifiedRow>
                            <VerifiedRow label="DKIM" verified={emailDomain.dkimVerified}>
                                <DnsRecord
                                    type="TXT"
                                    name={`cfai._domainkey.${emailDomain.domain}`}
                                    value={emailDomain.dkimPublicKey}
                                    purpose="Cryptographic signature"
                                />
                            </VerifiedRow>
                            <VerifiedRow label="DMARC" verified={emailDomain.dmarcVerified}>
                                <DnsRecord
                                    type="TXT"
                                    name={`_dmarc.${emailDomain.domain}`}
                                    value="v=DMARC1; p=none; rua=mailto:dmarc@contentflashai.com"
                                    purpose="Email fraud protection policy"
                                />
                            </VerifiedRow>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

// ---- Small reusable components ----

function Section({ title, children }) {
    return (
        <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">{title}</h3>
            <div className="space-y-3">
                {children}
            </div>
        </div>
    )
}

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
            {children}
        </div>
    )
}

function ColorField({ label, value, onChange }) {
    return (
        <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label}</label>
            <div className="flex items-center gap-2">
                <input
                    type="color"
                    value={value || "#6366f1"}
                    onChange={e => onChange(e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer border border-gray-200 dark:border-gray-600 p-0.5"
                />
                <input
                    type="text"
                    value={value || ""}
                    onChange={e => onChange(e.target.value)}
                    placeholder="#6366f1"
                    className="flex-1 text-xs border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    maxLength={20}
                />
            </div>
        </div>
    )
}

function DnsRecord({ type, name, value, purpose, required }) {
    const [copied, setCopied] = useState(false)
    const copy = (text) => {
        navigator.clipboard?.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }
    return (
        <div className={`rounded-lg border overflow-hidden text-xs ${required ? "border-amber-300 dark:border-amber-600" : "border-gray-200 dark:border-gray-600"}`}>
            <div className="bg-gray-50 dark:bg-gray-700/50 px-3 py-1.5 flex justify-between items-center">
                <span className="font-medium text-gray-600 dark:text-gray-300">
                    <span className="bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded mr-2">{type}</span>
                    {purpose}
                </span>
                {required && (
                    <span className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide flex-shrink-0">
                        Required for verification
                    </span>
                )}
            </div>
            <div className="px-3 py-2 space-y-1">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-gray-500 w-10 flex-shrink-0">Name</span>
                    <code className="flex-1 font-mono text-gray-800 dark:text-gray-200 break-all">{name}</code>
                    <button onClick={() => copy(name)} className="text-indigo-600 hover:underline flex-shrink-0">{copied ? "✓" : "Copy"}</button>
                </div>
                <div className="flex items-start justify-between gap-2">
                    <span className="text-gray-500 w-10 flex-shrink-0">Value</span>
                    <code className="flex-1 font-mono text-gray-800 dark:text-gray-200 break-all">{value}</code>
                    <button onClick={() => copy(value)} className="text-indigo-600 hover:underline flex-shrink-0">{copied ? "✓" : "Copy"}</button>
                </div>
            </div>
        </div>
    )
}

function EmbedCode({ domain }) {
    const code = `<script src="https://${domain}/embed.js" defer></script>\n<div data-cfai-form="YOUR_FORM_ID"></div>`
    const [copied, setCopied] = useState(false)
    return (
        <div className="mt-3">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Embed code (uses your custom domain):</p>
            <div className="relative">
                <pre className="text-xs bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 overflow-x-auto text-gray-800 dark:text-gray-200 pr-16">{code}</pre>
                <button
                    onClick={() => { navigator.clipboard?.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
                    className="absolute top-2 right-2 text-xs text-indigo-600 hover:underline bg-white dark:bg-gray-800 px-1.5 py-0.5 rounded"
                >
                    {copied ? "Copied!" : "Copy"}
                </button>
            </div>
        </div>
    )
}

function StatusBadge({ status }) {
    const map = {
        active: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
        pending_verification: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
        failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
        partial: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
        pending: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
        disabled: "bg-gray-100 text-gray-500"
    }
    const label = {
        active: "Active",
        pending_verification: "Awaiting verification",
        failed: "Verification failed",
        partial: "Partially verified",
        pending: "Pending",
        disabled: "Disabled"
    }
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${map[status] || map.pending}`}>
            {label[status] || status}
        </span>
    )
}

function SslBadge({ status }) {
    if (status === "active") return <span className="text-green-600 font-medium">✓ Active</span>
    if (status === "provisioning") return <span className="text-yellow-600">Provisioning (may take a few minutes)</span>
    if (status === "error") return <span className="text-red-600">Error — contact support</span>
    return <span className="text-gray-500">Pending</span>
}

function VerifiedRow({ label, verified, children }) {
    return (
        <div>
            <div className="flex items-center gap-2 mb-1.5">
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs ${verified ? "bg-green-500 text-white" : "bg-gray-200 dark:bg-gray-600 text-gray-500"}`}>
                    {verified ? "✓" : "·"}
                </span>
                <span className={`text-xs font-medium ${verified ? "text-green-700 dark:text-green-400" : "text-gray-600 dark:text-gray-400"}`}>
                    {label} {verified ? "verified" : "not verified"}
                </span>
            </div>
            {!verified && children}
        </div>
    )
}

const inputCls = "w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
